/**
 * Electrochemistry drill bank.
 *
 * Covers four core skills high-school / Pre-AP students must master:
 *   1. Assign oxidation numbers
 *   2. Identify what is oxidised vs reduced (and the agents)
 *   3. Predict galvanic-cell EMF from standard reduction potentials
 *   4. Recall the parts and direction of a galvanic / electrolytic cell
 *
 * Standard reduction potentials are at 25 °C, 1 M, 1 atm.
 */

export type EChemCategory =
  | "ox-number"
  | "redox-id"
  | "cell-emf"
  | "cell-anatomy"
  | "electrolysis";

export type EChemDifficulty = "easy" | "medium" | "hard";

export const ECHEM_CATEGORY_LABELS: Record<EChemCategory, string> = {
  "ox-number": "Oxidation Numbers",
  "redox-id": "Identify Redox",
  "cell-emf": "Cell EMF",
  "cell-anatomy": "Cell Anatomy",
  electrolysis: "Electrolysis",
};

export interface MultipleChoiceProblem {
  id: string;
  category: EChemCategory;
  difficulty: EChemDifficulty;
  prompt: string;
  /** Optional formula / equation to show in monospace under the prompt. */
  formula?: string;
  options: string[];
  /** Index of correct option in `options`. */
  correct: number;
  /** One-line teaching insight shown after grading. */
  insight: string;
}

/** Standard reduction potentials, V (vs SHE). Selection commonly taught. */
export const STANDARD_POTENTIALS: Array<{
  half: string;
  E: number;
}> = [
  { half: "F₂ + 2e⁻ → 2F⁻", E: 2.87 },
  { half: "Au³⁺ + 3e⁻ → Au", E: 1.5 },
  { half: "Cl₂ + 2e⁻ → 2Cl⁻", E: 1.36 },
  { half: "O₂ + 4H⁺ + 4e⁻ → 2H₂O", E: 1.23 },
  { half: "Ag⁺ + e⁻ → Ag", E: 0.8 },
  { half: "Fe³⁺ + e⁻ → Fe²⁺", E: 0.77 },
  { half: "Cu²⁺ + 2e⁻ → Cu", E: 0.34 },
  { half: "2H⁺ + 2e⁻ → H₂", E: 0.0 },
  { half: "Pb²⁺ + 2e⁻ → Pb", E: -0.13 },
  { half: "Sn²⁺ + 2e⁻ → Sn", E: -0.14 },
  { half: "Ni²⁺ + 2e⁻ → Ni", E: -0.25 },
  { half: "Fe²⁺ + 2e⁻ → Fe", E: -0.44 },
  { half: "Zn²⁺ + 2e⁻ → Zn", E: -0.76 },
  { half: "Al³⁺ + 3e⁻ → Al", E: -1.66 },
  { half: "Mg²⁺ + 2e⁻ → Mg", E: -2.37 },
  { half: "Na⁺ + e⁻ → Na", E: -2.71 },
  { half: "Li⁺ + e⁻ → Li", E: -3.04 },
];

export const ECHEM_PROBLEMS: MultipleChoiceProblem[] = [
  // ── oxidation numbers ────────────────────────────────────────────────────
  {
    id: "ox-1",
    category: "ox-number",
    difficulty: "easy",
    prompt: "What is the oxidation number of S in H₂SO₄?",
    formula: "H₂SO₄",
    options: ["+2", "+4", "+6", "−2"],
    correct: 2,
    insight: "H = +1 (×2 = +2), O = −2 (×4 = −8). Sum to 0 → S = +6.",
  },
  {
    id: "ox-2",
    category: "ox-number",
    difficulty: "easy",
    prompt: "What is the oxidation number of Mn in KMnO₄?",
    formula: "KMnO₄",
    options: ["+2", "+4", "+6", "+7"],
    correct: 3,
    insight: "K = +1, O = −2 (×4 = −8). Sum to 0 → Mn = +7 (its highest).",
  },
  {
    id: "ox-3",
    category: "ox-number",
    difficulty: "medium",
    prompt: "What is the oxidation number of Cr in Cr₂O₇²⁻?",
    formula: "Cr₂O₇²⁻",
    options: ["+3", "+6", "+7", "+12"],
    correct: 1,
    insight:
      "O = −2 (×7 = −14). Sum to charge −2 → 2Cr = +12 → each Cr = +6.",
  },
  {
    id: "ox-4",
    category: "ox-number",
    difficulty: "medium",
    prompt: "What is the oxidation number of N in NH₄⁺?",
    formula: "NH₄⁺",
    options: ["−3", "+1", "+3", "+5"],
    correct: 0,
    insight: "H = +1 (×4 = +4). Sum to +1 → N = −3.",
  },
  {
    id: "ox-5",
    category: "ox-number",
    difficulty: "hard",
    prompt: "What is the oxidation number of O in H₂O₂ (hydrogen peroxide)?",
    formula: "H₂O₂",
    options: ["−2", "−1", "0", "+1"],
    correct: 1,
    insight:
      "Peroxides are the exception — O is −1, not −2. H = +1 (×2) so 2O must = −2 → each O = −1.",
  },

  // ── identify redox ───────────────────────────────────────────────────────
  {
    id: "rid-1",
    category: "redox-id",
    difficulty: "easy",
    prompt: "In Zn + Cu²⁺ → Zn²⁺ + Cu, which species is oxidised?",
    formula: "Zn + Cu²⁺ → Zn²⁺ + Cu",
    options: ["Zn", "Cu²⁺", "Cu", "Zn²⁺"],
    correct: 0,
    insight: "Zn loses 2 e⁻ (0 → +2). LEO: Loss of Electrons = Oxidation.",
  },
  {
    id: "rid-2",
    category: "redox-id",
    difficulty: "easy",
    prompt: "In Zn + Cu²⁺ → Zn²⁺ + Cu, which species is the oxidising agent?",
    formula: "Zn + Cu²⁺ → Zn²⁺ + Cu",
    options: ["Zn", "Cu²⁺", "Cu", "Zn²⁺"],
    correct: 1,
    insight:
      "The oxidising agent is itself reduced. Cu²⁺ gains 2 e⁻ → Cu, so Cu²⁺ does the oxidising.",
  },
  {
    id: "rid-3",
    category: "redox-id",
    difficulty: "medium",
    prompt: "In 2Al + 3Cl₂ → 2AlCl₃, what is the change in oxidation number for Al?",
    formula: "2Al + 3Cl₂ → 2AlCl₃",
    options: ["0 → +1", "0 → +3", "+3 → 0", "0 → −3"],
    correct: 1,
    insight: "Element Al is 0; in AlCl₃, Al is +3. Loss of 3 e⁻ = oxidation.",
  },
  {
    id: "rid-4",
    category: "redox-id",
    difficulty: "medium",
    prompt:
      "Which of the following is NOT a redox reaction?",
    options: [
      "2H₂ + O₂ → 2H₂O",
      "Mg + 2HCl → MgCl₂ + H₂",
      "AgNO₃ + NaCl → AgCl + NaNO₃",
      "Fe + CuSO₄ → FeSO₄ + Cu",
    ],
    correct: 2,
    insight:
      "Precipitation reactions like AgNO₃ + NaCl involve no change in oxidation number — just a swap of partners.",
  },

  // ── galvanic cell EMF ────────────────────────────────────────────────────
  {
    id: "emf-1",
    category: "cell-emf",
    difficulty: "easy",
    prompt:
      "Calculate E°cell for a Zn|Zn²⁺ ‖ Cu²⁺|Cu galvanic cell. (E°(Zn²⁺/Zn) = −0.76 V, E°(Cu²⁺/Cu) = +0.34 V)",
    formula: "E°cell = E°cathode − E°anode",
    options: ["+0.42 V", "+1.10 V", "−1.10 V", "+0.10 V"],
    correct: 1,
    insight: "Cathode = Cu (higher E°). E°cell = 0.34 − (−0.76) = +1.10 V.",
  },
  {
    id: "emf-2",
    category: "cell-emf",
    difficulty: "medium",
    prompt:
      "For a Mg|Mg²⁺ ‖ Ag⁺|Ag cell: E°(Mg²⁺/Mg) = −2.37 V, E°(Ag⁺/Ag) = +0.80 V. What is E°cell?",
    options: ["+1.57 V", "+3.17 V", "−3.17 V", "+1.17 V"],
    correct: 1,
    insight: "E°cell = E°cathode − E°anode = 0.80 − (−2.37) = +3.17 V (spontaneous).",
  },
  {
    id: "emf-3",
    category: "cell-emf",
    difficulty: "medium",
    prompt:
      "A galvanic cell has E°cell > 0. What does this tell you about ΔG°?",
    options: [
      "ΔG° > 0, non-spontaneous",
      "ΔG° = 0, equilibrium",
      "ΔG° < 0, spontaneous",
      "Cannot determine",
    ],
    correct: 2,
    insight: "ΔG° = −nFE°cell. Positive E° → negative ΔG° → spontaneous.",
  },
  {
    id: "emf-4",
    category: "cell-emf",
    difficulty: "hard",
    prompt:
      "Which metal will spontaneously reduce Cu²⁺ to Cu? Use the activity series (more negative E° = stronger reducing agent).",
    options: ["Ag", "Au", "Zn", "Pt"],
    correct: 2,
    insight:
      "Zn (E° = −0.76 V) is below Cu (E° = +0.34 V) in the activity series, so Zn will reduce Cu²⁺.",
  },

  // ── cell anatomy ─────────────────────────────────────────────────────────
  {
    id: "anat-1",
    category: "cell-anatomy",
    difficulty: "easy",
    prompt: "In a galvanic cell, oxidation occurs at the:",
    options: ["Cathode", "Anode", "Salt bridge", "Voltmeter"],
    correct: 1,
    insight: 'Mnemonic: "An Ox" — Anode = Oxidation. "Red Cat" — Reduction at Cathode.',
  },
  {
    id: "anat-2",
    category: "cell-anatomy",
    difficulty: "easy",
    prompt:
      "In a galvanic (voltaic) cell, electrons flow through the external wire from:",
    options: [
      "Cathode to anode",
      "Anode to cathode",
      "Salt bridge to anode",
      "Cathode to salt bridge",
    ],
    correct: 1,
    insight:
      "Electrons leave the anode (where they're produced by oxidation) and travel to the cathode through the wire.",
  },
  {
    id: "anat-3",
    category: "cell-anatomy",
    difficulty: "medium",
    prompt:
      "What is the role of the salt bridge in a galvanic cell?",
    options: [
      "It carries electrons between half-cells.",
      "It maintains charge balance by allowing ion flow.",
      "It produces the EMF of the cell.",
      "It prevents any current from flowing.",
    ],
    correct: 1,
    insight:
      "Cations migrate toward the cathode and anions toward the anode through the salt bridge — without it, charge buildup stops the reaction.",
  },
  {
    id: "anat-4",
    category: "cell-anatomy",
    difficulty: "medium",
    prompt: "In a galvanic cell, the anode is the ____ electrode.",
    options: ["positive", "negative", "neutral", "depends on concentration"],
    correct: 1,
    insight:
      "In a GALVANIC cell, anode = negative (electrons pushed out). In an ELECTROLYTIC cell, the polarity flips — anode is positive.",
  },

  // ── electrolysis ─────────────────────────────────────────────────────────
  {
    id: "ely-1",
    category: "electrolysis",
    difficulty: "easy",
    prompt: "Electrolysis is best described as:",
    options: [
      "A spontaneous redox reaction producing electricity.",
      "Using electrical energy to drive a non-spontaneous redox reaction.",
      "A combustion reaction in a sealed cell.",
      "Heating a metal until it ionises.",
    ],
    correct: 1,
    insight:
      "Electrolysis is the reverse of a galvanic cell — you input energy to FORCE a non-spontaneous reaction (E°cell < 0).",
  },
  {
    id: "ely-2",
    category: "electrolysis",
    difficulty: "medium",
    prompt:
      "During electrolysis of molten NaCl, what is produced at the cathode?",
    formula: "2NaCl(l) → 2Na(l) + Cl₂(g)",
    options: ["Cl₂ gas", "Na metal", "NaOH", "H₂ gas"],
    correct: 1,
    insight:
      "Cathode = reduction. Na⁺ + e⁻ → Na(l). Cl⁻ is oxidised to Cl₂ at the anode.",
  },
  {
    id: "ely-3",
    category: "electrolysis",
    difficulty: "medium",
    prompt:
      "In an electrolytic cell, the cathode is connected to the ____ terminal of the power supply.",
    options: ["positive", "negative", "ground", "either, polarity is arbitrary"],
    correct: 1,
    insight:
      "The negative terminal pushes electrons INTO the cathode, where reduction occurs. (Opposite sign from a galvanic anode/cathode!)",
  },
  {
    id: "ely-4",
    category: "electrolysis",
    difficulty: "hard",
    prompt:
      "How many moles of electrons are needed to electroplate 1 mole of Al from Al³⁺?",
    formula: "Al³⁺ + 3e⁻ → Al",
    options: ["1 mol", "2 mol", "3 mol", "6 mol"],
    correct: 2,
    insight:
      "Look at the half-reaction: 3 e⁻ are required per Al³⁺. Use this with Faraday's constant (96,485 C/mol e⁻) for charge calculations.",
  },
];